import React, { useCallback, useMemo, useState } from 'react';

import {
  AccountFormContext,
  type AccountFormContextValue,
  type AccountFormStep,
  ENTRY_STEP,
} from './AccountFormContext';

export interface AccountFormProviderProps extends Pick<
  AccountFormContextValue,
  'onSuccess' | 'onError' | 'redirectTo'
> {
  children: React.ReactNode;
  /**
   * Seeds the shared `email` so the entry field opens on an address the caller
   * already knows — a link that carries one, or a resolved session. Read once,
   * at mount, so a value that arrives later needs `setEmail` instead.
   */
  initialEmail?: string;
  /**
   * Opens on a step other than entry, for a surface that has already done what
   * the entry screen does — reception's phone-number page knows the account and
   * only needs the registration screen's details. Read once, at mount, like
   * `initialEmail`; modules navigate with `setStep` from there.
   */
  initialStep?: AccountFormStep;
}

/**
 * Owns the shared email/step state and provides it to whatever modules
 * render as children. Generic across whichever module set composes it —
 * `<AccountFormV2 />` is one such composition (this provider plus the
 * default module set), not the only one.
 */
export const AccountFormProvider: React.FC<AccountFormProviderProps> = ({
  onSuccess,
  onError,
  redirectTo,
  initialEmail,
  initialStep,
  children,
}) => {
  const [email, setEmail] = useState(initialEmail ?? '');
  const [step, setStepState] = useState(initialStep ?? ENTRY_STEP);
  const [formError, setFormError] = useState<AccountFormContextValue['formError']>(null);

  // The alert region is screen-level state now, so leaving the screen has to
  // drop it: without this a failed lookup on one step would still be on show
  // after the next step replaced the module that reported it.
  const setStep = useCallback((next: AccountFormStep) => {
    setFormError(null);
    setStepState(next);
  }, []);

  const context = useMemo<AccountFormContextValue>(
    () => ({
      onSuccess,
      onError,
      redirectTo,
      email,
      setEmail,
      step,
      setStep,
      formError,
      setFormError,
    }),
    [onSuccess, onError, redirectTo, email, step, setStep, formError],
  );

  return <AccountFormContext.Provider value={context}>{children}</AccountFormContext.Provider>;
};
